import React, { useCallback, useEffect, useRef, useState } from 'react';
import FaseGrupoEquipeModal from '../../tournament/FaseGrupoEquipeModal';
import {
  desvincularEquipeDoGrupo,
  listarEquipesDoGrupo,
  vincularEquipeAoGrupo,
} from '../../../services/faseGrupoPartidaEquipeService';
import type { FaseGrupoPartidaEquipe } from '../../../types/graphql';
import { Tooltip } from '@mui/material';

export type FaseGrupoEquipesCardProps = {
  fase: { id: string; nome: string };
  faseCategoriaTorneioId: string;
  categoriaTorneioId: string;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
};

const FaseGrupoEquipesCard: React.FC<FaseGrupoEquipesCardProps> = ({
  fase,
  faseCategoriaTorneioId,
  categoriaTorneioId,
  onEdit,
  onDelete,
}) => {
  const [vinculos, setVinculos] = useState<FaseGrupoPartidaEquipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchVinculos = useCallback(
    async (initial = false) => {
      if (!isMountedRef.current) {
        return;
      }

      if (initial) {
        setLoading(true);
      } else {
        setReloading(true);
      }

      setError(null);

      try {
        const data = await listarEquipesDoGrupo(fase.id);
        if (!isMountedRef.current) {
          return;
        }
        setVinculos(data);
      } catch (err) {
        if (!isMountedRef.current) {
          return;
        }
        const message = err instanceof Error ? err.message : 'Nao foi possivel carregar as equipes do grupo.';
        setError(message);
      } finally {
        if (!isMountedRef.current) {
          return;
        }
        if (initial) {
          setLoading(false);
        } else {
          setReloading(false);
        }
      }
    },
    [fase.id],
  );

  useEffect(() => {
    fetchVinculos(true).catch(() => undefined);
  }, [fetchVinculos]);

  const handleConfirmAdicionar = useCallback(
    async (equipeId: string) => {
      await vincularEquipeAoGrupo({ faseGrupoPartidasId: fase.id, equipeId });
      await fetchVinculos();
    },
    [fase.id, fetchVinculos],
  );

  const handleRemoverEquipe = useCallback(
    async (vinculoId: string) => {
      if (!window.confirm('Deseja remover esta equipe do grupo?')) {
        return;
      }

      try {
        setError(null);
        setRemovingId(vinculoId);
        const removed = await desvincularEquipeDoGrupo(vinculoId);
        if (!removed) {
          throw new Error('Nao foi possivel remover a equipe do grupo.');
        }
        await fetchVinculos();
      } catch (err) {
        if (isMountedRef.current) {
          const message = err instanceof Error ? err.message : 'Nao foi possivel remover a equipe do grupo.';
          setError(message);
        }
      } finally {
        if (isMountedRef.current) {
          setRemovingId(null);
        }
      }
    },
    [fetchVinculos],
  );


  const renderEquipeItem = (vinculo: FaseGrupoPartidaEquipe) => {
    const equipe = vinculo.equipe;
    const atletas = equipe?.atletas ?? [];
    const previewAtletas = atletas.slice(0, 2);

    return (
      <table
        key={vinculo.id}
        className="table table-borderless align-middle w-100 border-bottom mb-0"
        data-testid={`equipe-item-${vinculo.id}`}
        style={{ tableLayout: 'fixed' }} // ajuda as colunas a respeitarem os widths
      >
        <colgroup>
          <col style={{ width: '78%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '10%' }} />
        </colgroup>

        <tbody>
          {/* Linha 1: título/label + V + D + botão remover */}
          <tr>
            <td className="py-1">
              <div className="d-flex justify-content-between align-items-start">
                <div>Atletas</div>

                <button
                  type="button"
                  className="btn btn-link text-danger p-0"
                  aria-label="Remover equipe"
                  onClick={() => handleRemoverEquipe(vinculo.id)}
                  disabled={removingId === vinculo.id || loading || reloading}
                  data-testid={`btn-remover-equipe-${vinculo.id}`}
                >
                  {removingId === vinculo.id ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  ) : (
                    <i className="bi bi-trash" aria-hidden="true" />
                  )}
                </button>
              </div>
            </td>

            <td className="py-1 text-center border-start">
              V
            </td>

            <td className="py-1 text-center border-start">
              D
            </td>

            <td className="py-1 text-end">
              
            </td>
          </tr>

          {/* Linha 2: prévia dos atletas + valores V/D (0/0) */}
          <tr className="border-top">
            <td className="py-2">
              {previewAtletas.length > 0 ? (
                <div className="d-flex align-items-center gap-2">
                  {previewAtletas.map((atleta, index) => (
                    <div
                      key={atleta?.id ?? `atleta-${vinculo.id}-${index}`}
                      className="d-flex flex-column align-items-center"
                      style={{ width: '50%' }}
                    >
                      <img
                        src={atleta?.usuario?.imagemUrl || '/assets/images/user.png'}
                        alt={atleta?.usuario?.nome || 'Atleta'}
                        className="rounded-circle"
                        width={40}
                        height={40}
                        style={{ objectFit: 'cover' }}
                      />
                      <span className="text-truncate d-block" style={{ fontSize: 13, maxWidth: '100%', textAlign: 'center' }}>
                        {atleta?.usuario?.nome ?? 'Sem nome'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Nenhum atleta vinculado
                </div>
              )}
            </td>

            <td className="py-2 text-center border-start">{equipe.vitoriasGrupo ?? 0}</td>
            <td className="py-2 text-center border-start">{equipe.derrotasGrupo ?? 0}</td>
            <td className="py-2" />
          </tr>
        </tbody>
      </table>
    );
  };

  return (
    <div
      className="card shadow-sm w300"
      data-testid={`grupo-equipes-card-${fase.id}`}
    >
      <div className="card-header bg-light">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <i className="bi bi-trophy me-2" aria-hidden="true" />
            <span>{fase.nome}</span>
          </div>
          <div className="d-flex align-items-center gap-3">
            {reloading && !loading && (
              <span className="spinner-border spinner-border-sm text-secondary" role="status" aria-hidden="true" />
            )}
            <div>
              <button
                type="button"
                aria-label="Adicionar equipe"
                className="btn btn-link p-0 me-2"
                onClick={() => setModalOpen(true)}
                data-testid={`btn-adicionar-equipe-${fase.id}`}
              >
                <Tooltip title="Adicionar equipe" placement='top' arrow>
                  <i className="bi bi-plus-circle" aria-hidden="true" />
                </Tooltip>
              </button>
              <button
                type="button"
                aria-label="Editar fase"
                className="btn btn-link p-0 me-2"
                onClick={() => onEdit?.(fase.id)}
                data-testid="fase-edit"
              >
                <Tooltip title="Editar fase" placement='top' arrow>
                  <i className="bi bi-pencil" aria-hidden="true" />
                </Tooltip>
              </button>
              <button
                type="button"
                aria-label="Excluir fase"
                className="btn btn-link p-0"
                onClick={() => onDelete?.(fase.id)}
                data-testid="fase-delete"
              >
                <Tooltip title="Excluir fase" placement='top' arrow>
                  <i className="bi bi-trash" aria-hidden="true" />
                </Tooltip>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="card-body p-0">
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {loading ? (
          <div className="placeholder-glow">
            <p className="placeholder col-6 mb-2" />
            <p className="placeholder col-4" />
            <span className="visually-hidden">Carregando...</span>
          </div>
        ) : vinculos.length === 0 ? (
          <div className="text-muted p-2" data-testid={`empty-equipes-${fase.id}`}>
            Nenhuma equipe neste grupo
          </div>
        ) : (
          <div
            className="d-flex flex-wrap gap-0"
            data-testid={`lista-equipes-${fase.id}`}
          >
            {vinculos.map(renderEquipeItem)}
          </div>
        )}
      </div>
      <FaseGrupoEquipeModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        faseGrupoPartidasId={fase.id}
        faseCategoriaTorneioId={faseCategoriaTorneioId}
        categoriaTorneioId={categoriaTorneioId}
        onConfirm={handleConfirmAdicionar}
      />
    </div>
  );
};

export default FaseGrupoEquipesCard;
